import React, { useRef, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';

const StatCard = ({ number, label, unit, delay = 0 }) => {
  const [count, setCount] = useState(0);
  const [ref, inView] = useInView({ threshold: 0.3, triggerOnce: true });
  const targetNumber = parseInt(number.replace('+', ''));

  useEffect(() => {
    if (inView) {
      const timer = setTimeout(() => {
        let start = 0;
        const increment = targetNumber / 30;
        const counter = setInterval(() => {
          start += increment;
          if (start >= targetNumber) {
            setCount(targetNumber);
            clearInterval(counter);
          } else {
            setCount(Math.floor(start));
          }
        }, 50);
        return () => clearInterval(counter);
      }, delay);
      return () => clearTimeout(timer);
    }
  }, [inView, targetNumber, delay]);

  return (
    <div ref={ref} className={`text-center transition-all duration-1000 ${inView ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`} style={{ transitionDelay: `${delay}ms` }}>
      <div className="relative group">
        <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-cyan-500 rounded-2xl blur opacity-25 group-hover:opacity-50 transition duration-300"></div>
        <div className="relative bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
          <div className="text-5xl md:text-6xl font-black bg-gradient-to-br from-blue-600 to-cyan-500 bg-clip-text text-transparent mb-3">
            {count}{number.includes('+') ? '+' : ''}{unit}
          </div>
          <div className="text-gray-700 font-semibold text-lg leading-tight">
            {label}
          </div>
        </div>
      </div>
    </div>
  );
};

export const NewStatsSection = () => {
  const stats = [
    { number: "300+", label: "People Die Per Day In The US Due To Drug Overdoses", unit: "" },
    { number: "30", label: "Of prescriptions go unused each year", unit: "%" },
    { number: "6.8", label: "Billion prescriptions were issued in America last year", unit: "B" },
    { number: "100", label: "CFR Compliant Solution", unit: "%" }
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-white via-gray-50 to-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-6">
            Critical Statistics
          </div>
          <h2 className="text-4xl md:text-6xl font-black text-gray-900 mb-6">
            The <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Problem</span> We Solve
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Every day, dangerous medications and illicit drugs pose serious risks to communities nationwide. NarcX™ provides the solution.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              number={stat.number}
              label={stat.label}
              unit={stat.unit}
              delay={index * 200}
            />
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center px-6 py-3 rounded-full bg-gradient-to-r from-red-100 to-orange-100 text-red-800 text-sm font-semibold mb-4">
            <div className="w-2 h-2 bg-red-500 rounded-full mr-3 animate-pulse"></div>
            Critical Need for Action
          </div>
          <p className="text-lg text-gray-700 max-w-2xl mx-auto">
            Created by a retired Federal Special Agent who oversaw diversion. <span className="font-semibold text-gray-900">It's time for a better solution.</span>
          </p>
        </div>
      </div>
    </section>
  );
};